import React, { useEffect, useMemo, useState } from 'react';
import { apiClient, ACCESS_LEVELS } from '@rti/shared';
import { trackGoogleAnalyticsEvent } from '@/utils/googleAnalytics';
import TermsConditions from '@/components/TermsConditions';
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter';
import RoleFields from '@/components/RoleFields';
import { Mail } from 'lucide-react'

declare global {
  interface Window { grecaptcha?: any }
}

const FreeTrial: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showVerificationPending, setShowVerificationPending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const showSupportLink = ((import.meta as any).env?.VITE_SHOW_SUPPORT_LINK as string | undefined) === 'true';

  type MembershipId = typeof ACCESS_LEVELS[keyof typeof ACCESS_LEVELS];

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirm: '',
    company_name: '',
    buying_role: '',
    job_title: '',
    membership_id: ACCESS_LEVELS.BASE as MembershipId,
    free_trial_duration_days: 60,
  });
  const [buyingRoleOther, setBuyingRoleOther] = useState('');

  const recaptchaSiteKey = (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY as string | undefined;

  const [freeEmailDomains, setFreeEmailDomains] = useState<Set<string>>(new Set());
  const [domainCheck, setDomainCheck] = useState<{ eligible?: boolean; company_name?: string | null; reason?: string } | null>(null);
  const [checkingDomain, setCheckingDomain] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const domains = await apiClient.getFreeEmailDomains();
        if (Array.isArray(domains) && domains.length > 0) {
          setFreeEmailDomains(new Set(domains.map((d) => String(d).toLowerCase())));
        }
      } catch {
        setFreeEmailDomains(new Set(['gmail.com','yahoo.com','hotmail.com','outlook.com']));
      }
    })();
  }, []);

  const isFreeEmailDomain = (email: string): boolean => {
    const domain = (email || '').toLowerCase().split('@')[1];
    if (!domain) return false;
    return freeEmailDomains.has(domain);
  };

  const deriveCompanyFromEmail = (email: string): string => {
    const domain = (email || '').toLowerCase().split('@')[1];
    if (!domain) return '';
    const base = domain.split('.')[0];
    if (!base) return '';
    return base.charAt(0).toUpperCase() + base.slice(1);
  };

  // Check domain eligibility when email changes (debounced)
  useEffect(() => {
    const email = form.email.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setDomainCheck(null);
      return;
    }
    if (isFreeEmailDomain(email)) {
      setDomainCheck({ eligible: false, reason: 'Please use a company email address' });
      return;
    }
    setCheckingDomain(true);
    const timer = setTimeout(async () => {
      try {
        const result = await apiClient.checkDomainEligibility(email);
        setDomainCheck(result);
        if (result?.eligible && !result.company_name) {
          setForm((f) => f.company_name ? f : { ...f, company_name: deriveCompanyFromEmail(email) });
        }
      } catch {
        setDomainCheck(null);
      } finally {
        setCheckingDomain(false);
      }
    }, 600);
    return () => { clearTimeout(timer); setCheckingDomain(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email, freeEmailDomains]);

  // Derive human-friendly membership name from ACCESS_LEVELS by reverse lookup
  const membershipName = useMemo(() => {
    const entries = Object.entries(ACCESS_LEVELS) as Array<[string, number]>;
    const match = entries.find(([_, id]) => id === (form.membership_id || 1));
    const key = match?.[0] || 'BASIC';
    // Convert e.g. 'PREMIUM' -> 'Premium'
    return key.charAt(0) + key.slice(1).toLowerCase();
  }, [form.membership_id]);

  // Decode base64 (URL-safe) helper
  const b64DecodeNum = (s: string | null | undefined): number | null => {
    if (!s) return null;
    try {
      const fixed = s.replace(/-/g, '+').replace(/_/g, '/');
      const padLen = (4 - (fixed.length % 4)) % 4;
      const padded = fixed + '='.repeat(padLen);
      const decoded = atob(padded);
      const n = parseInt(decoded, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  };

  // Read obfuscated params from URL: m = base64(membership_id), t = base64(trial_days)
  /*
  m=1 → MQ== (URL-safe: MQ)
  m=2 → Mg== (URL-safe: Mg)
  m=3 → Mw== (URL-safe: Mw)
  t=30 → MzA= (URL-safe: MzA)
  t=90 → OTA= (URL-safe: OTA)
  Standard: /free-trial?m=Mg==&t=MzA=
  URL-safe: /free-trial?m=Mg&t=MzA
 /free-trial?m=Mg&t=OTA
   */
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const m = b64DecodeNum(params.get('m'));
      const t = b64DecodeNum(params.get('t'));
      const allowedMemberships: number[] = Object.entries(ACCESS_LEVELS).filter(([name]) => name !== 'EMPLOYEE').map(([, id]) => id);
      const prefill_email = params.get('e') || params.get('email') || '';
      const prefill_fn = params.get('fn') || params.get('first_name') || '';
      const prefill_ln = params.get('ln') || params.get('last_name') || '';
      const prefill_company = params.get('c') || params.get('company') || params.get('company_name') || '';
      
      const membershipId: MembershipId = (m !== null && allowedMemberships.includes(m)) 
        ? m as MembershipId 
        : ACCESS_LEVELS.BASE;
      
      setForm((f) => ({
        ...f,
        membership_id: membershipId,
        free_trial_duration_days: t ?? 60,
        email: prefill_email ? prefill_email : f.email,
        first_name: prefill_fn ? prefill_fn : f.first_name,
        last_name: prefill_ln ? prefill_ln : f.last_name,
        company_name: prefill_company ? prefill_company : f.company_name,
      }));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!recaptchaSiteKey) return;
    if (window.grecaptcha) return;
    const script = document.createElement('script');
    script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${recaptchaSiteKey}`;
    script.async = true;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [recaptchaSiteKey]);

  // Countdown timer for resend verification email
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const getRecaptchaToken = async (): Promise<string | undefined> => {
    if (!recaptchaSiteKey) return undefined;
    const waitForGrecaptcha = async (timeoutMs = 5000): Promise<any> => {
      const start = Date.now();
      while (!window.grecaptcha) {
        if (Date.now() - start > timeoutMs) return undefined;
        await new Promise((r) => setTimeout(r, 100));
      }
      return window.grecaptcha;
    };
    try {
      const grecaptcha = await waitForGrecaptcha();
      if (!grecaptcha) return undefined;
      await new Promise<void>((resolve) => {
        try { grecaptcha.ready(() => resolve()); } catch { resolve(); }
      });
      const token = await grecaptcha.execute(recaptchaSiteKey, { action: 'signup' });
      return typeof token === 'string' && token.length > 0 ? token : undefined;
    } catch {
      return undefined;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === 'membership_id' || name === 'free_trial_duration_days' ? Number(value) : value }));
    setError('');
  };

  // Real-time password match detection: only show mismatch once user types into confirm
  const passwordsMismatch = useMemo(() => {
    return (
      (form.password?.length || 0) > 0 &&
      (form.password_confirm?.length || 0) > 0 &&
      form.password !== form.password_confirm
    );
  }, [form.password, form.password_confirm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.first_name || !form.last_name || !form.email || !form.password || !form.password_confirm) {
      setError('Please fill in all required fields');
      return;
    }
    const joiningExistingCompany = !!domainCheck?.eligible && !!domainCheck?.company_name;
    if (!joiningExistingCompany && !form.company_name.trim()) {
      setError('Please enter your company name');
      return;
    }
    if (!form.buying_role) {
      setError('Please select your role');
      return;
    }
    if (form.buying_role === 'Other' && !buyingRoleOther.trim()) {
      setError('Please describe your role');
      return;
    }
    if (form.password !== form.password_confirm) {
      setError('Passwords do not match');
      return;
    }
    if (!agreedToTerms) {
      setError('You must agree to the terms and conditions');
      return;
    }
    // Client-side block for free email domains
    if (isFreeEmailDomain(form.email)) {
      setError('Please use a company email address.');
      return;
    }
    if (domainCheck && !domainCheck.eligible) {
      setError(domainCheck.reason || 'Please use a company email address.');
      return;
    }

    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken();
      const resp = await apiClient.signupFreeTrial({
        user: {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          password: form.password,
          password_confirm: form.password_confirm,
          company_name: form.company_name || undefined,
          buying_role: form.buying_role === 'Other' ? buyingRoleOther.trim() : form.buying_role,
          job_title: form.job_title.trim() || undefined,
          membership_id: form.membership_id || 1,
          free_trial_duration_days: form.free_trial_duration_days || 60,
        },
        recaptchaToken,
      });

      // NEW: Handle the new response format (no auto-login)
      if (resp && (resp as any).success) {
        trackGoogleAnalyticsEvent('sign_up', { method: 'free_trial' });
        // Show verification pending screen
        setShowVerificationPending(true);
      } else {
        setError('Signup failed');
        return;
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedToTerms(e.target.checked);
    if (e.target.checked) setShowTerms(true);
  };

  const handleTermsLinkClick = () => setShowTerms(true);

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    try {
      await apiClient.resendVerification(form.email);
      setResendCountdown(60);
      setError('Verification email sent! Please check your inbox and spam folder.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  // If verification pending, show the pending screen
  if (showVerificationPending) {
    return (
      <div className="root">
        <div className="auth-root flex w-full h-[100vh]">
          <div className="flex flex-col justify-between items-center h-full w-full max-w-2xl px-8 py-10 bg-rs-black text-rs-off-white mx-auto">
            <a href="/">
              <img src="/logos/Primary/White.svg" alt="Resin Smart Logo" className="w-sm mb-8" />
            </a>
            <div>
              <div className="mb-4">
                <Mail className={'text-rs-dark-green mx-auto mb-4'} size={85} />
              </div>

              <h1 className="text-3xl font-light mb-8">Verify Your Email Address</h1>
            </div>
            <div className="text-center max-w-lg">

              <div className="mb-4">
                <p className="text-2xl text-rs-dark-green mb-2">
                  We've sent a verification email to:
                </p>

                <p className="text-xl text-rs-off-white mb-10 pb-5">
                  {form.email}
                </p>

                <div className="bg-rs-jet border border-rs-jet rounded-lg p-6 mb-6 text-left">
                  <h2 className="text-lg font-semibold mb-3 text-rs-green">Next Steps:</h2>
                  <ol className="flex flex-col gap-2 text-rs-off-white/90">
                    <li className="flex items-start">
                      <span className="text-rs-green mr-2">1.</span>
                      <span>Check your email inbox for a message from ResinSmart</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-rs-green mr-2">2.</span>
                      <span>Click the verification link in the email</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-rs-green mr-2">3.</span>
                      <span>You'll be automatically logged in and redirected to your dashboard</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-rs-dark-green rounded-lg p-4 mb-6">
                  <p className=" text-rs-off-black">
                    <strong>Can't find the email?</strong><br/>The email should arrive within a few minutes.<br/>Check your spam or junk folder.

                  </p>
                </div>

                {error && (
                  <div className={`text-sm mb-4 ${error.includes('sent') ? 'text-rs-green' : 'text-rs-red'}`}>
                    {error}
                  </div>
                )}

                <button
                  onClick={handleResendVerification}
                  disabled={loading || resendCountdown > 0}
                  className="text-rs-green hover:text-rs-light-green underline disabled:opacity-50"
                >
                  {loading ? 'Sending...' : resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend verification email'}
                </button>

                { showSupportLink && (
                  <p className="mt-6 text-sm text-rs-off-white/60">
                  Need help? <a href="mailto:support@resinsmart.ai" className="text-rs-green underline">Contact Support</a>
                </p>
                )}
              </div>
            </div>
            <div></div>
          </div>
          <div className="art-section flex-1 relative overflow-hidden">
            <img
              className="art-section-dashboard w-full max-w-8xl rounded-xl border-4 border-rs-jet"
              src="/dashboard-landing.png"
              alt="RTi Dashboard Preview"
            />
            <div className="patterns-container absolute inset-0 pointer-events-none">
              <img src="/patterns/union-green.svg" className="w-2xl absolute top-10 left-10" alt="" />
              <img src="/patterns/union-green.svg" className="w-2xl absolute bottom-10 right-10" alt="" />
            </div>
          </div>


        </div>
      </div>
    );
  }

  return (
    <div className="root">
      <div className="auth-root flex w-full h-[100vh]">
        <div className="flex flex-col justify-between items-start h-full w-full max-w-lg px-5 py-10 gap-10 bg-rs-black text-rs-off-white">
          <a href="/">
            <img src="/logos/Primary/White.svg" alt="Resin Smart Logo" className="w-xs" />
          </a>
          <form onSubmit={handleSubmit} className="lg:ml-5 flex flex-col max-w-md w-full" data-hs-ignore="true">
            <h1 className="mb-14 text-3xl font-light">{`Start your ${form.free_trial_duration_days}-day free trial today!`}
              <br />
              <span className={'text-xl uppercase text-rs-dark-green'}>{`${membershipName} Membership`}</span></h1>
            <span className="text-xl dark:text-rs-off-white">Create your account</span>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <input className="p-3 rounded-sm bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none" name="first_name" placeholder="First name" value={form.first_name} onChange={handleChange} />
              <input className="p-3 rounded-sm bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none" name="last_name" placeholder="Last name" value={form.last_name} onChange={handleChange} />
            </div>

            <input className={`mt-3 p-3 rounded-sm w-full bg-transparent border ${domainCheck && !domainCheck.eligible ? 'border-rs-red' : 'border-rs-jet'} text-rs-off-white placeholder-rs-off-white/60 focus:outline-none ${domainCheck && !domainCheck.eligible ? '' : 'focus:border-rs-green'}`} name="email" type="email" placeholder="Email (company email required)" value={form.email} onChange={handleChange} />
            {checkingDomain && <div className="text-xs text-rs-off-white/40 mt-1">Checking domain...</div>}
            {domainCheck && !domainCheck.eligible && <div className="text-xs text-rs-red mt-1">{domainCheck.reason}</div>}
            {domainCheck?.eligible && domainCheck.company_name && <div className="text-xs text-rs-green mt-1">Your account will be linked to {domainCheck.company_name}</div>}

            {!(domainCheck?.eligible && domainCheck?.company_name) && (
              <input className="mt-3 p-3 rounded-sm w-full bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none" name="company_name" placeholder="Company name" value={form.company_name} onChange={handleChange} required />
            )}

            <RoleFields
              buyingRole={form.buying_role}
              buyingRoleOther={buyingRoleOther}
              jobTitle={form.job_title}
              onChange={handleChange}
              onBuyingRoleOtherChange={setBuyingRoleOther}
            />

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="relative">
                <input className="p-3 rounded-sm w-full bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none" name="password" type={showPassword ? 'text' : 'password'} placeholder="Password" value={form.password} onChange={handleChange} />
                <button type="button" className="text-sm absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowPassword((s) => !s)}>{showPassword ? 'Hide' : 'Show'}</button>
              </div>
              <div className="relative">
                <input
                  className={`p-3 rounded-sm w-full bg-transparent border ${passwordsMismatch ? 'border-rs-red' : 'border-rs-jet'} text-rs-off-white placeholder-rs-off-white/60 focus:outline-none ${passwordsMismatch ? '' : 'focus:border-rs-green'}`}
                  name="password_confirm"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirm Password"
                  value={form.password_confirm}
                  onChange={handleChange}
                  aria-invalid={passwordsMismatch}
                />
                <button type="button" className="text-sm absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowConfirm((s) => !s)}>{showConfirm ? 'Hide' : 'Show'}</button>
                {passwordsMismatch && (
                  <div className="text-rs-red mt-1 text-xs">Passwords do not match</div>
                )}
              </div>
            </div>

            {/* Strength meter (shared) */}
            <PasswordStrengthMeter password={form.password} showLabel showFeedback className="mt-2" />

            {/* Hidden values carried in state: membership_id and free_trial_duration_days */}

            {/* Terms and Conditions */}
            <div className="mt-3 text-rs-off-white flex items-start justify-start">
              <input
                type="checkbox"
                id="agree-terms"
                checked={agreedToTerms}
                onChange={handleCheckboxChange}
                className="mt-1 mr-3"
              />
              <label htmlFor="agree-terms" className="text-sm text-rs-off-white">
                By clicking this box and using this site, you are agreeing to the ResinSmart.ai{' '}
                <button type="button" onClick={handleTermsLinkClick} className="text-rs-green hover:text-rs-light-green underline">terms and conditions</button>
              </label>
            </div>

            {error && <div className="text-rs-red mt-3 text-sm">{error}</div>}

            <button className="mt-4 w-sm green text-lg" type="submit" disabled={loading || !agreedToTerms || checkingDomain || (domainCheck !== null && !domainCheck.eligible)}>
              {loading ? 'Creating your account...' : 'Start Free Trial'}
            </button>

            {recaptchaSiteKey && (
              <p className="mt-3 text-xs text-rs-off-white/60">
                This site is protected by reCAPTCHA and the Google
                <a className="underline ml-1" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                {' '}and{' '}
                <a className="underline" href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                {' '} apply.
              </p>
            )}

            <p className="mt-3 text-sm text-rs-off-white/60">Already have an account? <a href="/authorization" className="text-rs-green underline">Sign in</a></p>
          </form>
          <div className="steps"></div>
        </div>
        <div className="art-section flex-1 relative overflow-hidden">
          <img
            className="art-section-dashboard w-full max-w-8xl rounded-xl border-4 border-rs-jet"
            src="/dashboard-landing.png"
            alt="RTi Dashboard Preview"
          />
          <div className="patterns-container absolute inset-0 pointer-events-none">
            <img src="/patterns/union-green.svg" className="w-2xl absolute top-10 left-10" alt="" />
            <img src="/patterns/union-green.svg" className="w-2xl absolute bottom-10 right-10" alt="" />
          </div>
        </div>
      </div>
      <TermsConditions isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </div>
  );
};

export default FreeTrial;
